import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SearchClient, algoliasearch } from "algoliasearch";
import {
  InstantSearch,
  useConfigure,
  useHits,
  useSearchBox,
} from "react-instantsearch";
import {
  AlgoliaEventObject,
  AlgoliaIndexType,
} from "common/interfaces/Algolia";
import type { GuidedEditPayload } from "common/interfaces/adsManagerGuidedEdit";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "common/interfaces/adsManagerGuidedEdit";
import actions from "src/actions";
import { FiSearch } from "react-icons/fi";
import { formatEventDateWithYear } from "src/utils";

export type AdAccountOption = { id: string; name: string };

/** Payload from GET .../guided-edit (shared with API contract). */
export type { GuidedEditPayload };

/** Canonical IANA zone for guided schedule wiring (event wall clock). */
export function normalizeGuidedFlowEventTimezone(
  value: string | null | undefined
): string {
  const t = value?.trim();
  return t || GUIDED_EDIT_DEFAULT_TIMEZONE;
}

export interface GuidedFlowDraftCreate {
  mode: "create";
  eventId: string;
  adAccountId?: string;
  /** IANA zone for the selected event — set with {@link normalizeGuidedFlowEventTimezone}(event.timezone). */
  eventTimezone: string;
  event: AlgoliaEventObject;
}

export interface GuidedFlowDraftEdit {
  mode: "edit";
  campaignId: string;
  adsetId: string;
  guidedEditPayload: GuidedEditPayload;
  eventId: string;
  adAccountId: string;
  eventTimezone: string;
  event?: AlgoliaEventObject;
}

/** New ad set (add): same guided flow, but on save we create a new ad set instead of updating. */
export interface GuidedFlowDraftNewAdset {
  mode: "edit_new_adset";
  campaignId: string;
  guidedEditPayload: GuidedEditPayload;
  eventId: string;
  adAccountId: string;
  eventTimezone: string;
  event?: AlgoliaEventObject;
}

/** Add creative(s) to existing ad set without editing targeting/budget/schedule. */
export interface GuidedFlowDraftNewCreative {
  mode: "edit_new_creative";
  campaignId: string;
  /** Meta Graph campaign id when published; used when `guidedEditPayload` is not loaded. */
  campaignExternalId?: string | null;
  adsetId?: string;
  guidedEditPayload?: GuidedEditPayload;
  creativeTargetAdsets: {
    id: string;
    name: string;
    disabled?: boolean;
  }[];
  initialCreativeTargetAdsetIds?: string[];
  eventId: string;
  adAccountId: string;
  eventTimezone: string;
  event?: AlgoliaEventObject;
}

export type GuidedFlowDraft =
  | GuidedFlowDraftCreate
  | GuidedFlowDraftEdit
  | GuidedFlowDraftNewAdset
  | GuidedFlowDraftNewCreative;

export interface GuidedFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  adAccounts?: AdAccountOption[];
  onDraftReady: (draft: GuidedFlowDraft) => void;
}

function venueDisplay(
  venue: { name?: string; address?: string } | undefined
): string {
  if (!venue) return "";
  const parts = [venue.name, venue.address].filter(Boolean);
  return parts.join(" ");
}

function EventSearchInner({
  onSelect,
  inputRef,
}: {
  onSelect: (event: AlgoliaEventObject) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const { query, refine } = useSearchBox();
  const { hits } = useHits<AlgoliaEventObject>();

  const nowUnix = Math.floor(Date.now() / 1000);
  useConfigure({
    restrictSearchableAttributes: ["name"],
    hitsPerPage: 10,
    facetFilters: [["status:OPEN"]],
    numericFilters: [`start_at_unix > ${nowUnix}`],
  });

  return (
    <VStack align="stretch" spacing={2}>
      <Box position="relative" width="100%">
        <Box position="relative" width="100%">
          <Box as="label" display="block" position="relative">
            <Box
              position="absolute"
              left={3}
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              zIndex={1}
            >
              <FiSearch color="gray" />
            </Box>
            <Box
              as="input"
              ref={inputRef as React.RefObject<HTMLInputElement>}
              placeholder="Select or search for event"
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                refine(e.target.value)
              }
              bg="white"
              pl={10}
              pr={3}
              py={2}
              borderWidth="1px"
              borderRadius="md"
              width="100%"
              _focus={{
                outline: "none",
                borderColor: "blue.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
              }}
            />
          </Box>
        </Box>
        {query.length > 0 && (
          <Box
            position="absolute"
            left={0}
            right={0}
            top="100%"
            mt={1}
            borderWidth="1px"
            borderRadius="md"
            maxH="240px"
            overflowY="auto"
            bg="white"
            boxShadow="md"
            zIndex={10}
          >
            {hits.length === 0 ? (
              <Box p={3}>
                <Text fontSize="sm" color="gray.500">
                  No events found.
                </Text>
              </Box>
            ) : (
              hits.map((hit) => (
                <Box
                  key={hit.objectID}
                  data-testid={`event-search-hit-${hit.objectID}`}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => onSelect(hit)}
                  borderBottomWidth={1}
                  borderColor="gray.100"
                  _last={{ borderBottomWidth: 0 }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Text fontWeight="medium" noOfLines={1}>
                      {hit.name}
                    </Text>
                    {hit.is_external ? (
                      <Badge colorScheme="blue">
                        External
                      </Badge>
                    ) : null}
                  </Box>
                  <Text fontSize="sm" color="gray.600">
                    {formatEventDateWithYear(
                      hit.start_at,
                      hit.timezone
                    )}{" "}
                    {hit.venue?.name ? ` · ${hit.venue.name}` : ""}
                  </Text>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>
    </VStack>
  );
}

function EventSearch({
  indexName,
  searchClient,
  onSelect,
  inputRef,
}: {
  indexName: string;
  searchClient: SearchClient;
  onSelect: (event: AlgoliaEventObject) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <EventSearchInner onSelect={onSelect} inputRef={inputRef} />
    </InstantSearch>
  );
}

const CreateCampaignFlowModal = ({
  isOpen,
  onClose,
  adAccounts: _adAccounts = [],
  onDraftReady,
}: GuidedFlowModalProps) => {
  const [eventsAlgoliaConfig, setEventsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);
  const [loadingAlgolia, setLoadingAlgolia] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AlgoliaEventObject | null>(
    null
  );
  const eventSearchInputRef = useRef<HTMLInputElement | null>(null);

  const fetchAlgoliaConfig = useCallback(async () => {
    setLoadingAlgolia(true);
    try {
      const response = await actions.ADMIN.fetchAlgoliaConfig({
        indexType: AlgoliaIndexType.EVENTS,
      });
      if (response.events_config) {
        const search = algoliasearch(
          response.events_config.app_id,
          response.events_config.api_key
        );
        search.clearCache?.();
        setEventsAlgoliaConfig({
          indexName: response.events_config.index_name,
          searchClient: search,
        });
      }
    } catch (e) {
      console.error("Error fetching Algolia config for event search:", e);
    } finally {
      setLoadingAlgolia(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAlgoliaConfig();
    }
  }, [isOpen, fetchAlgoliaConfig]);

  useEffect(() => {
    if (isOpen) return;
    setSelectedEvent(null);
  }, [isOpen]);

  const resolvedEventId = selectedEvent?.id ?? "";

  const handleSubmit = () => {
    if (!resolvedEventId || !selectedEvent) {
      return;
    }
    onDraftReady({
      mode: "create",
      eventId: resolvedEventId,
      eventTimezone: normalizeGuidedFlowEventTimezone(selectedEvent.timezone),
      event: selectedEvent,
    });
    setSelectedEvent(null);
    onClose();
  };

  const isValid = !!resolvedEventId && !!selectedEvent;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent borderRadius="2xl" borderColor="blue.500" borderWidth="2px">
        <ModalHeader pb="0">New Meta Campaign</ModalHeader>
        <ModalBody>
          <VStack spacing={1} align="stretch">
            <FormControl isRequired>
              <FormLabel requiredIndicator={null}>Campaign Event</FormLabel>
              {loadingAlgolia ? (
                <Box py={4}>
                  <Spinner size="sm" />
                </Box>
              ) : selectedEvent ? (
                <Box
                  borderWidth="1px"
                  borderRadius="lg"
                  boxShadow={"sm"}
                  p={3}
                  borderColor="gray.200"
                >
                  <Box display="flex" alignItems="flex-start" gap={3}>
                    {selectedEvent.image ? (
                      <Box w="64px" h="64px" boxShadow="lg">
                        <img
                          src={selectedEvent.image}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ) : null}
                    <VStack align="stretch" spacing={0} flex={1} minW={0}>
                      <Text fontWeight="600" noOfLines={2}>
                        {selectedEvent.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatEventDateWithYear(
                          selectedEvent.start_at,
                          selectedEvent.timezone
                        )}
                      </Text>
                      {selectedEvent.venue && (
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {venueDisplay(selectedEvent.venue)}
                        </Text>
                      )}
                    </VStack>
                    <Box display="flex" alignSelf="center">
                      <Button
                        variant="link"
                        colorScheme="blue"
                        size="sm"
                        onClick={() => setSelectedEvent(null)}
                      >
                        Change
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : eventsAlgoliaConfig ? (
                <EventSearch
                  indexName={eventsAlgoliaConfig.indexName}
                  searchClient={eventsAlgoliaConfig.searchClient}
                  onSelect={setSelectedEvent}
                  inputRef={eventSearchInputRef}
                />
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Unable to load event search.
                </Text>
              )}
            </FormControl>
            <Text fontSize="xs" fontWeight={"medium"} color="gray.600">
              The event name and date will also be the campaign name used to
              identify it in the Facebook Ads Manager.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose} width="50%">
            Cancel
          </Button>
          <Button
            colorScheme="green"
            color="white"
            onClick={handleSubmit}
            isDisabled={!isValid}
            _hover={{ bg: "green.600" }}
            _disabled={{
              bgColor: "gray.300",
              color: "white",
              _hover: { bgColor: "gray.300" },
              cursor: "not-allowed",
            }}
            width="50%"
          >
            Create Campaign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCampaignFlowModal;
